import React, { useEffect, useRef, useState } from 'react';

type Schema = Record<string, any>;
export function resolveMcpSchema(schema: Schema, value: any): Schema {
  const branch = (schema.oneOf ?? schema.anyOf ?? []).find((candidate: Schema) => {
    const discriminators = Object.entries(candidate.properties ?? {}).filter(([, child]: [string, any]) => child.const !== undefined);
    return discriminators.length > 0 && discriminators.every(([key, child]: [string, any]) => value?.[key] === child.const);
  });
  if (!branch) return schema;
  const hidden = new Set((branch.not?.anyOf ?? []).flatMap((rule: Schema) => rule.required?.length === 1 ? rule.required : []));
  const properties = Object.fromEntries(Object.entries(schema.properties ?? {}).filter(([key]) => !hidden.has(key))
    .map(([key, child]) => [key, { ...(child as Schema), ...branch.properties?.[key] }]));
  return { ...schema, properties, required: Array.from(new Set([...(schema.required ?? []), ...(branch.required ?? [])])) };
}

export function pruneMcpArguments(schema: Schema, value: any): any {
  const resolved = resolveMcpSchema(schema, value);
  if (!resolved.properties || !value || Array.isArray(value) || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).filter(([key, item]) => item !== undefined && (key in resolved.properties || resolved.additionalProperties === true))
    .map(([key, item]) => [key, pruneMcpArguments(resolved.properties[key] ?? {}, item)]));
}
interface Props {
  path: string;
  schema: Schema;
  value: any;
  onChange: (value: any) => void;
  onError: (path: string, error?: string) => void;
  disabled?: boolean;
}

/** Fixed objects stay structured; arbitrary JSON and arrays retain their JSON types. */
export default function McpSchemaField({ path, schema: inputSchema, value, onChange, onError, disabled }: Props) {
  const schema = resolveMcpSchema(inputSchema, value);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  useEffect(() => () => { onErrorRef.current(path); }, [path]);
  const [raw, setRaw] = useState(() => value === undefined ? '' : JSON.stringify(value, null, 2));
  useEffect(() => { setRaw(value === undefined ? '' : JSON.stringify(value, null, 2)); }, [value]);
  const type = Array.isArray(schema.type) ? schema.type.find((item: string) => item !== 'null') : schema.type;
  const objectSchema = type === 'object' || schema.properties;
  if (objectSchema && schema.properties) {
    const current = value && typeof value === 'object' ? value : {};
    const alternatives = schema.oneOf ?? schema.anyOf;
    return <fieldset className="mcp-object-fields" disabled={disabled}>
      <legend>{path}</legend>
      {alternatives?.some((choice: Schema) => choice.required?.length) && <p className="param-hint">
        Choose one: {alternatives.filter((choice: Schema) => choice.required?.length).map((choice: Schema) => choice.required.join(' + ')).join(' or ')}.
      </p>}
      {Object.entries(schema.properties).map(([key, child]: [string, any]) => <div className="param-field" key={key}>
        <label htmlFor={`mcp-${path}.${key}`}>{key}{schema.required?.includes(key) ? ' *' : ''}</label>
        <McpSchemaField path={`${path}.${key}`} schema={child} value={current[key]} disabled={disabled} onError={onError}
          onChange={next => {
            const updated = { ...current };
            if (next === undefined) delete updated[key]; else updated[key] = next;
            onChange(Object.keys(updated).length ? pruneMcpArguments(inputSchema, updated) : undefined);
          }} />
        {child.description && <span className="param-hint">{child.description}</span>}
      </div>)}
    </fieldset>;
  }
  if (schema.enum) return <select id={`mcp-${path}`} aria-label={path} className="form-input" value={value ?? ''} disabled={disabled}
    onChange={event => onChange(event.target.value === '' ? undefined : schema.enum.find((option: any) => String(option) === event.target.value))}>
    <option value="">Select…</option>{schema.enum.map((option: any) => <option key={String(option)} value={String(option)}>{String(option)}</option>)}
  </select>;
  if (type === 'boolean') return <select id={`mcp-${path}`} aria-label={path} className="form-input" disabled={disabled}
    value={value === undefined ? '' : String(value)} onChange={event => onChange(event.target.value === '' ? undefined : event.target.value === 'true')}>
    <option value="">Use default</option><option value="true">Yes</option><option value="false">No</option>
  </select>;
  if (type === 'number' || type === 'integer') return <input id={`mcp-${path}`} aria-label={path} type="number" className="form-input"
    disabled={disabled} value={value ?? ''} min={schema.minimum} max={schema.maximum} step={type === 'integer' ? 1 : 'any'}
    onChange={event => onChange(event.target.value === '' ? undefined : Number(event.target.value))} />;
  if (objectSchema || type === 'array') return <textarea id={`mcp-${path}`} aria-label={path} className="form-input" rows={4} disabled={disabled}
    value={raw} placeholder={type === 'array' ? '["value"]' : '{}'} onChange={event => {
      const text = event.target.value;
      setRaw(text);
      if (!text.trim()) { onError(path); onChange(undefined); return; }
      try {
        const next = JSON.parse(text);
        if (type === 'array' ? !Array.isArray(next) : !next || typeof next !== 'object' || Array.isArray(next)) throw new Error('shape');
        onError(path); onChange(next);
      } catch { onError(path, `${path} must be valid JSON ${type === 'array' ? 'array' : 'object'}.`); }
    }} />;
  return <input id={`mcp-${path}`} aria-label={path} type={schema.writeOnly ? 'password' : 'text'} className="form-input"
    autoComplete="off" disabled={disabled} value={value ?? ''} maxLength={schema.maxLength}
    onChange={event => onChange(event.target.value || undefined)} />;
}
